<template>
  <section class="filters" data-cy="product-filters-menu">
    <div class="filters__actions">
      <input
        type="button"
        value="Clear"
        class="filter-button-clear"
        @click.prevent="clearFilters"
        data-cy="clear-filters-button"
      />
      <input
        type="button"
        value="Apply"
        class="filter-button-apply"
        @click.prevent="applyFilters"
        data-cy="apply-filters-button"
      />
    </div>
    <ul class="filters__list">
      <li v-for="item in filters" :key="item.label" data-cy="filters-item">
        <label data-cy="filters-item-label">{{ item.label }}</label>
        <DropDownList
          class="filters-dropdown"
          :data-items="item.values"
          v-model="item.value"
          data-cy="filters-item-dropdown"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { EnumDpm } from "@/Domain/EnumDpm";
import type { OuKashUnionUkdProducts } from "@/Domain/OuKashUnionUkdProducts";
import { onBeforeMount, ref, watch } from "vue";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";

const props = defineProps<{
  filters: FilterMenuItem[];
  getPropValues: (field: string) => Promise<OuKashUnionUkdProducts[]>;
}>();

const emits = defineEmits<{
  (event: "apply", filters: FilterMenuItem[]): void;
  (event: "clear"): void;
  (event: "close"): void;
}>();

let dpmValues = Object.values(EnumDpm);
dpmValues = dpmValues.slice(0, dpmValues.length / 2);
const passoverValues = ["Y", "N"];
const ciValues = ["", "Consumer", "Consumer & Industrial", "Industrial"];
const conditions = ["Y", "N"];

const filters = ref([
  {
    field: "Symbol",
    label: "Symbol",
    placeholder: "Select a Symbol",
    values: [] as string[],
    value: "Select a Symbol",
  },
  {
    field: "AgencyName",
    label: "Agency",
    placeholder: "Select an Agency",
    values: [] as string[],
    value: "Select an Agency",
  },
  {
    field: "Dpm",
    label: "DPM",
    placeholder: "Select a DPM",
    values: [] as string[],
    value: "Select a DPM",
    getFilterValue: (value: string) => {
      return value;
    },
  },
  {
    field: "Pesach",
    label: "Passover",
    placeholder: "Select a Passover",
    values: passoverValues,
    value: "Select a Passover",
  },
  {
    field: "CI",
    label: "Consumer/Industrial",
    placeholder: "Select Consumer/Industrial",
    values: ciValues,
    value: "Select Consumer/Industrial",
  },
  {
    field: "Conditions",
    label: "Conditions",
    placeholder: "Select Conditions",
    values: conditions,
    value: "Select Conditions",
    getOpValue: (value: string) => {
      return value === "Y"
        ? "Is not null or whitespace"
        : "Is null or whitespace";
    },
    getFilterValue: (value: string) => {
      return null;
    },
  },
  {
    field: "CompanyCountry",
    label: "Country",
    placeholder: "Select a Country",
    values: [] as string[],
    value: "Select a Country",
  },
] as FilterMenuItem[]);

const setFilterValues = () => {
  props.filters.forEach((f) => {
    const filter = filters.value.find((item) => item.field === f.field);
    if (filter) {
      filter.value = f.value;
    }
  });
};

setFilterValues();

const applyFilters = () => {
  const setFilters = filters.value.filter((f) => f.value !== f.placeholder);

  emits("apply", setFilters);
  emits("close");
};

const clearFilters = () => {
  emits("apply", []);
  emits("close");
};

onBeforeMount(async () => {
  for (const filter of filters.value.filter((f) => f.values.length === 0)) {
    const values = await props.getPropValues(filter.field);
    filter.values = values.map((item) => (item as any)[filter.field]);
  }
});

watch(
  () => props.filters,
  () => {
    setFilterValues();
  }
);
</script>

<style lang="scss">
.filters {
  position: absolute;
  background: #fff;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.3);
  width: 100%;
  padding: 20px;
  overflow: auto;
  display: block;
  z-index: 100;
  height: 500px;

  &__actions {
    padding: 15px;
    border-bottom: 1px solid #ddd;
    margin: -20px -20px 10px;
    background: #efefef;
    justify-content: flex-end;
    display: flex;

    .filter-button-clear {
      text-align: center;
      padding: 8px 20px;
      box-shadow: none;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      background: 0 0;
      color: #3a5da6 !important;
      display: block;
      margin: 0 5px;
    }

    .filter-button-apply {
      text-align: center;
      color: #fff !important;
      padding: 8px 20px;
      box-shadow: none;
      background: #3a5da6;
      border: 1px solid #3a5da6;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      cursor: pointer;
      border-radius: 2px;
      display: block;
      margin: 0 5px;
    }
  }

  &__list {
    padding-left: 0;

    li {
      list-style-type: none;
    }

    label {
      font-weight: 500;
      font-size: 11px;
      text-transform: uppercase;
      color: #999;
      margin-bottom: 5px;
      display: inline-block;
    }

    .filters-dropdown {
      width: 100%;
      margin-bottom: 8px;
    }
  }
}
</style>
